import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import { parseCron, describeCron, nextRuns } from './utils/cronParser'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const EXAMPLES = [
  { expr: '* * * * *', label: 'Todo minuto' },
  { expr: '0 * * * *', label: 'Toda hora' },
  { expr: '0 3 * * *', label: 'Todo dia às 3h' },
  { expr: '0 9 * * 1-5', label: 'Dias úteis às 9h' },
  { expr: '*/15 * * * *', label: 'A cada 15 minutos' },
]

const CronExplainer = () => {
  useDocumentMeta({
    title: 'Explicador de Expressão Cron | jarder.dev',
    description:
      'Entenda o que uma expressão cron faz, em português, com as ' +
      'próximas execuções calculadas. Grátis, direto no navegador.',
  })

  const [expression, setExpression] = useState('0 9 * * 1-5')

  const parsed = useMemo(() => parseCron(expression), [expression])
  const runs = useMemo(() => {
    if (parsed.error) return []
    trackEvent('tool_generate', { tool: 'cron_explainer' })
    return nextRuns(parsed.fields, 5)
  }, [parsed])

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Explicador de Expressão Cron</h1>
      <p className="tool-disclaimer">
        Formato padrão de 5 campos: minuto, hora, dia do mês, mês, dia da
        semana (0 = domingo).
      </p>

      <div className="tool-field">
        <label htmlFor="cron-expression">Expressão cron</label>
        <input
          id="cron-expression"
          type="text"
          value={expression}
          onChange={(e) => setExpression(e.target.value)}
        />
      </div>

      <div className="tool-row" style={{ marginBottom: 20 }}>
        {EXAMPLES.map((ex) => (
          <button
            key={ex.expr}
            type="button"
            className="tool-button tool-button-secondary"
            onClick={() => setExpression(ex.expr)}
          >
            {ex.label}
          </button>
        ))}
      </div>

      {parsed.error && <p className="tool-error">{parsed.error}</p>}

      {!parsed.error && (
        <>
          <div className="tool-result-card">
            <span className="tool-result-label">O que ela faz</span>
            <span className="tool-result-value" style={{ fontSize: '1rem' }}>
              {describeCron(parsed.fields)}
            </span>
          </div>

          <ul className="tool-list">
            {runs.map((run, i) => (
              <li key={i}>
                {run.toLocaleString('pt-BR', {
                  weekday: 'short',
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="tool-info-section">
        <h2>Como ler uma expressão cron</h2>
        <p>
          Uma expressão cron tem 5 campos separados por espaço:{' '}
          <code>minuto hora dia-do-mês mês dia-da-semana</code>. Cada
          campo aceita um valor fixo, <code>*</code> (qualquer valor),
          listas (<code>1,3,5</code>), intervalos (<code>1-5</code>) ou
          passos (<code>*/15</code> = a cada 15 unidades). É o formato
          usado por cron jobs no Linux, GitHub Actions, Kubernetes
          CronJobs e agendadores de tarefas em geral.
        </p>
      </div>
    </div>
  )
}

export default CronExplainer
